import React from "react"
import { useTranslation } from "react-i18next"

import * as UserApi from "features/api/UserApi"
import { ErrorFn, http } from "services/http"
import browserStorageService, {
	localStorageService,
	sessionStorageService,
	STORAGE_KEY,
	ENTITY_ID_KEY,
	USER_ID_KEY,
} from "services/browserStorage"
import { useAlert } from "hooks/useAlert"

import {
	MFC_OPERATOR,
	BC_OPERATOR,
	MC_OPERATOR,
	ACTIVE_OPERATOR_STATUS,
	checkIsAdmin,
	checkIsAdminBicycode,
	checkIsAdminMFC,
	checkIsAdminMoustache,
	checkIsAdminOcode,
	checkIsAdminOperator,
} from "utils/constants"
import { Entity, ErrorResponse, User } from "models"

type AuthContextInterface = {
	entity: Entity
	user: User
	isLoggedIn: boolean
	isConnectedFromSupport: boolean
	isMFCOperator: boolean
	isBCOperator: boolean
	isMCOperator: boolean
	isAdminOCode: boolean
	isAdminBicycode: boolean
	isAdminMFC: boolean
	isAdminMoustache: boolean
	isAdminOperator: boolean
	isAdmin: boolean
	fetchEntity: (entity_id: string) => void
	fetchUser: (user_id: string, entity_id: string) => void
	login: () => void
	logout: () => void
	logoutAsSupport: () => void

	isUserDataCompleted: boolean
}

type Login = (
	mail?: string,
	password?: string,
	ignoreCurrentUser?: boolean
) => Promise<boolean | ErrorFn>

const AuthContext = React.createContext<AuthContextInterface | undefined>(undefined)

const useAuth = () => React.useContext(AuthContext)

const AuthProvider = ({ children }) => {
	const { t } = useTranslation()
	const { alert } = useAlert()

	const [entity, setEntity] = React.useState<Entity>(null)
	const [user, setUser] = React.useState<User>(null)

	const [isLoginInfoCompleted, setIsLoginInfoCompleted] = React.useState<boolean>(false)

	const isLoggedIn = React.useMemo(() => !!user, [user])

	// Entity operators
	const isMFCOperator = Boolean(
		entity?.data_operators?.find(
			(operator) =>
				operator.operator === MFC_OPERATOR && operator.status === ACTIVE_OPERATOR_STATUS
		)
	)
	const isBCOperator = Boolean(
		entity?.data_operators?.find(
			(operator) =>
				operator.operator === BC_OPERATOR && operator.status === ACTIVE_OPERATOR_STATUS
		)
	)
	const isMCOperator = Boolean(
		entity?.data_operators?.find(
			(operator) =>
				operator.operator === MC_OPERATOR && operator.status === ACTIVE_OPERATOR_STATUS
		)
	)

	// User
	const isAdminOCode = checkIsAdminOcode(user?.gender)
	const isAdminBicycode = checkIsAdminBicycode(user?.gender)
	const isAdminMFC = checkIsAdminMFC(user?.gender)
	const isAdminMoustache = checkIsAdminMoustache(user?.gender)
	const isAdminOperator = checkIsAdminOperator(user?.gender)
	const isAdmin = checkIsAdmin(user?.gender)

	const isConnectedFromSupport = React.useMemo(() => {
		return (
			sessionStorageService.get(STORAGE_KEY) &&
			sessionStorageService.get(USER_ID_KEY) === user?.user_id
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user])

	const isUserDataCompleted = React.useMemo(() => {
		return isLoginInfoCompleted
	}, [isLoginInfoCompleted])

	const fetchEntity = React.useCallback(async (entity_id: string) => {
		try {
			const { res: entityData, getError } = await UserApi.getEntity(entity_id)
			if (getError()) throw getError()

			setEntity(entityData)
			// if a sessionStorage has been init (for support purpose), prioritize editing the key in this storage
			browserStorageService.set(ENTITY_ID_KEY, entity_id, { priority: "sessionStorage" })
			return !!entityData
		} catch (error) {
			//console.error(error)
			return false
		}
	}, [])

	const fetchUser = React.useCallback(async (user_id: string, entity_id: string) => {
		try {
			const { res: userData, getError } = await UserApi.getUser(user_id)
			if (getError()) throw getError()
			const newUser = {
				...userData,
				entity_id,
			}
			setUser(newUser)
			// if a sessionStorage has been init (for support purpose), prioritize editing the key in this storage
			browserStorageService.set(USER_ID_KEY, user_id, { priority: "sessionStorage" })
			return !!newUser
		} catch (error) {
			//console.error(error)
			return false
		}
	}, []) // eslint-disable-line

	const login: Login = React.useCallback(
		async (mail, password, ignoreCurrentUser = false) => {
			// if the support try to connect as another user, do not read the current user
			if (user && !ignoreCurrentUser) return true

			let userEntityId: string
			let userId: string

			try {
				if (mail && password) {
					const { res: signInResponse, getError: getSignInError } = await UserApi.signIn(
						mail,
						password
					)
					if (getSignInError()) throw getSignInError()

					userEntityId = signInResponse?.entity_id
					userId = signInResponse?.user_id
				} else {
					userEntityId = browserStorageService.get(ENTITY_ID_KEY, {
						priority: "sessionStorage",
					})
					userId = browserStorageService.get(USER_ID_KEY, { priority: "sessionStorage" })
				}

				if (userEntityId && userId) {
					await fetchEntity(userEntityId)
					await fetchUser(userId, userEntityId)
				}
				return true
			} catch (error) {
				console.error(error)
				if ((error as ErrorResponse)?.message) {
					alert(
						"error",
						(error as ErrorResponse)?.message.toString() ||
							t("login_error", { ns: "auth" })
					)
				}
				logout()
				return false
			} finally {
				setIsLoginInfoCompleted(true)
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[user]
	)

	const logout = () => {
		http.clearConfig()
		localStorageService.remove(ENTITY_ID_KEY)
		localStorageService.remove(USER_ID_KEY)
		sessionStorageService.clear()
		setUser(null)
		setEntity(null)
	}

	const logoutAsSupport = async () => {
		http.clearSupportSession()
		sessionStorageService.clear()
		setUser(null)
		setEntity(null)
		// relog
		await login(null, null, true)
	}

	React.useEffect(() => {
		// Init http service
		http.initHttp({ logout, alert })
		// Auto-login
		// On app load, if a entity_id + token is available in browser storage, restore the user
		login()
	}, []) // eslint-disable-line

	return (
		<AuthContext.Provider
			value={{
				user,
				entity,
				isLoggedIn,
				isUserDataCompleted,
				isConnectedFromSupport,
				isMFCOperator,
				isBCOperator,
				isMCOperator,
				isAdminOCode,
				isAdminBicycode,
				isAdminMFC,
				isAdminMoustache,
				isAdminOperator,
				isAdmin,

				login,
				logout,
				logoutAsSupport,
				fetchEntity,
				fetchUser,
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}

export { useAuth, AuthProvider }
